import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { AxiosResponse, AxiosError } from "axios";
import { createAPIwithNextValue } from "../../../utils/createAPIwithNextValue";
import { generateDownloadString } from "../ClipsUtilities/generateDownloadString";
import { generateDownloadStringAILink } from "../ClipsUtilities/generateDownloadStringAILink";
import { generateDownloadStringSentinel } from "../ClipsUtilities/generateDownloadStringSentinel";

const startDate = new Date();

const endDate = new Date();
let activeSMTPRequestToken = "";

// Merge per-day precision/recall summary rows from an additional clips page
// into the accumulated summary. Rows for the same day (date_str) are combined
// by summing the counts and recomputing precision/recall, since a day's clips
// can span a page boundary.
export const mergeFrontelPrecisionRecallSummaries = (
  existing: any[],
  incoming: any[]
) => {
  const merged: any[] = (existing || []).map((row: any) => ({ ...row }));

  (incoming || []).forEach((row: any) => {
    const existingRow = merged.find(
      (item: any) => item.date_str === row.date_str
    );
    if (!existingRow) {
      merged.push({ ...row });
      return;
    }
    existingRow.true_positives += row.true_positives;
    existingRow.false_positives += row.false_positives;
    existingRow.false_negatives += row.false_negatives;
    existingRow.true_negatives += row.true_negatives;
    existingRow.unclassified += row.unclassified;
    existingRow.total_clips += row.total_clips;
    existingRow.precision =
      existingRow.true_positives + existingRow.false_positives > 0
        ? existingRow.true_positives /
          (existingRow.true_positives + existingRow.false_positives)
        : null;
    existingRow.recall =
      existingRow.true_positives + existingRow.false_negatives > 0
        ? existingRow.true_positives /
          (existingRow.true_positives + existingRow.false_negatives)
        : null;
  });

  return merged;
};

interface ClipsState {
  smtpData: any[];
  setSmtpData: (smtpData: any[]) => void;

  precisionRecallDataSMTP: any;
  setPrecisionRecallDataSMTP: (precisionRecallDataSMTP: any) => void;

  selectedRowSMTP: any;
  setSelectedRowSMTP: (selectedRowSMTP: any) => void;

  selectedClipSMTPMedia: any[];
  setSelectedClipSMTPMedia: (selectedClipSMTPMedia: any[]) => void;

  activeClipsTab: string;
  setActiveClipsTab: (activeClipsTab: string) => void;

  noDataToDisplaySMTP: boolean;
  setNoDataToDisplaySMTP: (noDataToDisplaySMTP: boolean) => void;

  filtersAppliedSMTP: boolean;
  setFiltersAppliedSMTP: (filtersAppliedSMTP: boolean) => void;

  fetchingFilteredDataSMTP: boolean;
  setFetchingFilteredDataSMTP: (fetchingFilteredDataSMTP: boolean) => void;

  userDropDownValueSMTP: string | null;
  setUserDropDownValueSMTP: (userDropDownValueSMTP: string | null) => void;

  cameraDropDownValueSMTP: string | null;
  setCameraDropDownValueSMTP: (cameraDropDownValueSMTP: string | null) => void;

  groupIDSMTP: number | null;
  setGroupIDSMTP: (groupIDSMTP: number | null) => void;

  groupOptionsSMTP: any[];
  setGroupOptionsSMTP: (groupOptionsSMTP: any[]) => void;

  loadingGroupsSMTP: boolean;
  setLoadingGroupsSMTP: (loadingGroupsSMTP: boolean) => void;

  siteDropDownValueSMTP: string | null;
  setSiteDropDownValueSMTP: (siteDropDownValueSMTP: string | null) => void;

  siteOptionsSMTP: any[];
  setSiteOptionsSMTP: (siteOptionsSMTP: any[]) => void;

  loadingSitesSMTP: boolean;
  setLoadingSitesSMTP: (loadingSitesSMTP: boolean) => void;

  smtpSite: {
    site_id: string;
    site_name: string;
  };
  setSmtpSite: (smtpSite: { site_id: string; site_name: string }) => void;

  selectedRowAlertsSMTP: any[];
  setSelectedRowAlertsSMTP: (selectedRowAlertsSMTP: any[]) => void;

  loadingCamerasSMTP: boolean;
  setLoadingCamerasSMTP: (loadingCamerasSMTP: boolean) => void;

  userFiltersSMTP: any;
  setUserFiltersSMTP: (userFiltersSMTP: any | ((prev: any) => any)) => void;

  cameraOptionsSMTP: any[];

  setCameraOptionsSMTP: (cameraOptionsSMTP: any[]) => void;

  timeFiltersDisabledSMTP: boolean;
  setTimeFiltersDisabledSMTP: (timeFiltersDisabledSMTP: boolean) => void;

  pageLoadingSMTP: boolean;
  setPageLoadingSMTP: (pageLoadingSMTP: boolean) => void;

  pageSMTP: number;
  setPageSMTP: (pageSMTP: number) => void;

  videoClipLoadingSMTP: boolean;
  setVideoClipLoadingSMTP: (videoClipLoadingSMTP: boolean) => void;

  loadingAdditionalDataSMTP: boolean;
  setLoadingAdditionalDataSMTP: (loadingAdditionalDataSMTP: boolean) => void;

  clipsIndexOrderSMTP: any;
  setClipsIndexOrderSMTP: (clipsIndexOrderSMTP: any) => void;

  allAlertsDialogOpen: boolean;
  setAllAlertsDialogOpen: (allAlertsDialogOpen: boolean) => void;

  falsePositiveReductionDialogOpen: boolean;
  setFalsePositiveReductionDialogOpen: (
    falsePositiveReductionDialogOpen: boolean
  ) => void;

  requestCounterSMTP: number;
  setRequestCounterSMTP: (requestCounterSMTP: number) => void;

  downloadURLSMTP: string;
  setDownloadURLSMTP: (downloadURLSMTP: string) => void;

  fetchingDownloadURLSMTP: boolean;
  setFetchingDownloadURLSMTP: (fetchingDownloadURLSMTP: boolean) => void;

  downloadURLAILink: string;
  setDownloadURLAILink: (downloadURLAILink: string) => void;

  fetchingDownloadURLAILink: boolean;
  setFetchingDownloadURLAILink: (fetchingDownloadURLAILink: boolean) => void;

  downloadURLSentinel: string;
  setDownloadURLSentinel: (downloadURLSentinel: string) => void;

  fetchingDownloadURLSentinel: boolean;
  setFetchingDownloadURLSentinel: (
    fetchingDownloadURLSentinel: boolean
  ) => void;

  smtpTableFilter: string;
  setSmtpTableFilter: (smtpTableFilter: string) => void;

  siteTimeZoneSMTP: string;
  setSiteTimeZoneSMTP: (siteTimeZoneSMTP: string) => void;

  getUserGroupsSMTP: (navigate: NavigateFunction) => void;

  getAllSitesClipsSMTP: (
    groupID: number | null,
    navigate: NavigateFunction
  ) => void;

  getCamerasBySiteSMTP: (siteIds: string[], navigate: NavigateFunction) => void;

  getSMTPData: (navigate: NavigateFunction) => void;

  getVideoURLSMTP: (file_id: string, navigate: NavigateFunction) => void;

  getDownloadURLSMTP: (navigate: NavigateFunction) => void;

  getDownloadURLAILink: (
    navigate: NavigateFunction,
    userFilters: any,
    forceRefresh?: boolean,
    groupID?: number | null
  ) => void;

  getDownloadURLSentinel: (
    navigate: NavigateFunction,
    userFiltersSentinel: any,
    forceRefresh?: boolean
  ) => void;

  showFrontelTab: boolean;
  setShowFrontelTab: (showFrontelTab: boolean) => void;

  frontelData: any[];
  setFrontelData: (frontelData: any[]) => void;

  precisionRecallDataFrontel: any;
  setPrecisionRecallDataFrontel: (precisionRecallDataFrontel: any) => void;

  selectedRowFrontel: any;
  setSelectedRowFrontel: (selectedRowFrontel: any) => void;

  selectedClipFrontelMedia: any[];
  setSelectedClipFrontelMedia: (selectedClipFrontelMedia: any[]) => void;

  noDataToDisplayFrontel: boolean;
  setNoDataToDisplayFrontel: (noDataToDisplayFrontel: boolean) => void;

  showMoreDialogOpenFrontel: boolean;
  setShowMoreDialogOpenFrontel: (showMoreDialogOpenFrontel: boolean) => void;

  verifiedClickedFrontel: boolean;
  setVerifiedClickedFrontel: (verifiedClickedFrontel: boolean) => void;

  unverifiedClickedFrontel: boolean;
  setUnverifiedClickedFrontel: (unverifiedClickedFrontel: boolean) => void;

  filtersAppliedFrontel: boolean;
  setFiltersAppliedFrontel: (filtersAppliedFrontel: boolean) => void;

  fetchingFilteredDataFrontel: boolean;
  setFetchingFilteredDataFrontel: (
    fetchingFilteredDataFrontel: boolean
  ) => void;

  userDropDownValueFrontel: string | null;
  setUserDropDownValueFrontel: (
    userDropDownValueFrontel: string | null
  ) => void;

  cameraDropDownValueFrontel: string | null;
  setCameraDropDownValueFrontel: (
    cameraDropDownValueFrontel: string | null
  ) => void;

  groupIDFrontel: number | null;
  setGroupIDFrontel: (groupIDFrontel: number | null) => void;

  frontelUsername: string;
  setFrontelUsername: (frontelUsername: string) => void;

  groupOptionsFrontel: any[];
  setGroupOptionsFrontel: (groupOptionsFrontel: any[]) => void;

  loadingGroupsFrontel: boolean;
  setLoadingGroupsFrontel: (loadingGroupsFrontel: boolean) => void;

  siteDropDownValueFrontel: string | null;
  setSiteDropDownValueFrontel: (
    siteDropDownValueFrontel: string | null
  ) => void;

  siteOptionsFrontel: any[];
  setSiteOptionsFrontel: (siteOptionsFrontel: any[]) => void;

  loadingSitesFrontel: boolean;
  setLoadingSitesFrontel: (loadingSitesFrontel: boolean) => void;

  frontelSite: {
    site_id: string;
    site_name: string;
  };
  setFrontelSite: (frontelSite: { site_id: string; site_name: string }) => void;

  selectedRowAlertsFrontel: any[];
  setSelectedRowAlertsFrontel: (selectedRowAlertsFrontel: any[]) => void;

  loadingCamerasFrontel: boolean;
  setLoadingCamerasFrontel: (loadingCamerasFrontel: boolean) => void;

  userFiltersFrontel: any;
  setUserFiltersFrontel: (
    userFiltersFrontel: any | ((prev: any) => any)
  ) => void;

  cameraOptionsFrontel: any[];
  setCameraOptionsFrontel: (cameraOptionsFrontel: any[]) => void;

  timeFiltersDisabledFrontel: boolean;
  setTimeFiltersDisabledFrontel: (timeFiltersDisabledFrontel: boolean) => void;

  pageLoadingFrontel: boolean;
  setPageLoadingFrontel: (pageLoadingFrontel: boolean) => void;

  pageFrontel: number;
  setPageFrontel: (pageFrontel: number) => void;

  videoClipLoadingFrontel: boolean;
  setVideoClipLoadingFrontel: (videoClipLoadingFrontel: boolean) => void;

  videoURLFrontel: string;
  setVideoURLFrontel: (videoURLFrontel: string) => void;

  clipsIndexOrderFrontel: any;
  setClipsIndexOrderFrontel: (clipsIndexOrderFrontel: any) => void;

  loadingAdditionalDataFrontel: boolean;
  setLoadingAdditionalDataFrontel: (
    loadingAdditionalDataFrontel: boolean
  ) => void;

  getUserGroupsFrontel: (navigate: NavigateFunction) => void;

  getAllSitesFrontel: (
    groupID: number | null,
    navigate: NavigateFunction
  ) => void;

  getCamerasBySiteFrontel: (
    siteIds: string[],
    navigate: NavigateFunction
  ) => void;

  getSiteTimeZone: (site_id: string, navigate: NavigateFunction) => void;

  requestCounterFrontel: number;
  setRequestCounterFrontel: (requestCounterFrontel: number) => void;

  getFrontelData: (navigate: NavigateFunction) => void;

  getClipInfoFrontel: (file_id: string, navigate: NavigateFunction) => void;

  getVideoURLFrontel: (file_id: string, navigate: NavigateFunction) => void;

  resetState: () => void;
}

/** Sequence numbers of the latest report-link request per tab; see getDownloadURLSentinel. */
const reportRequestIds = { Sentinel: 0, AILink: 0 };

export const useClipsStore = create<ClipsState>((set, get) => ({
  smtpData: [],
  setSmtpData: (smtpData: any[]) => set({ smtpData }),

  precisionRecallDataSMTP: {},
  setPrecisionRecallDataSMTP: (precisionRecallDataSMTP: any) =>
    set({ precisionRecallDataSMTP }),

  selectedRowSMTP: {},
  setSelectedRowSMTP: (selectedRowSMTP: any) => set({ selectedRowSMTP }),

  selectedClipSMTPMedia: [],
  setSelectedClipSMTPMedia: (selectedClipSMTPMedia: any[]) =>
    set({ selectedClipSMTPMedia }),

  activeClipsTab: "",
  setActiveClipsTab: (activeClipsTab: string) => set({ activeClipsTab }),

  noDataToDisplaySMTP: true,
  setNoDataToDisplaySMTP: (noDataToDisplaySMTP: boolean) =>
    set({ noDataToDisplaySMTP }),

  filtersAppliedSMTP: false,
  setFiltersAppliedSMTP: (filtersAppliedSMTP: boolean) =>
    set({ filtersAppliedSMTP }),

  fetchingFilteredDataSMTP: false,
  setFetchingFilteredDataSMTP: (fetchingFilteredDataSMTP: boolean) =>
    set({ fetchingFilteredDataSMTP }),

  userDropDownValueSMTP: null,
  setUserDropDownValueSMTP: (userDropDownValueSMTP: string | null) =>
    set({ userDropDownValueSMTP }),

  cameraDropDownValueSMTP: null,
  setCameraDropDownValueSMTP: (cameraDropDownValueSMTP: string | null) =>
    set({ cameraDropDownValueSMTP }),

  groupIDSMTP: null,
  setGroupIDSMTP: (groupIDSMTP: number | null) => set({ groupIDSMTP }),

  groupOptionsSMTP: localStorage.parentGroups
    ? JSON.parse(localStorage.parentGroups)
    : [],
  setGroupOptionsSMTP: (groupOptionsSMTP: any[]) => set({ groupOptionsSMTP }),

  loadingGroupsSMTP: false,
  setLoadingGroupsSMTP: (loadingGroupsSMTP: boolean) =>
    set({ loadingGroupsSMTP }),

  siteDropDownValueSMTP: null,
  setSiteDropDownValueSMTP: (siteDropDownValueSMTP: string | null) =>
    set({ siteDropDownValueSMTP }),

  siteOptionsSMTP: [],
  setSiteOptionsSMTP: (siteOptionsSMTP: any[]) => set({ siteOptionsSMTP }),

  loadingSitesSMTP: false,
  setLoadingSitesSMTP: (loadingSitesSMTP: boolean) => set({ loadingSitesSMTP }),

  smtpSite: {
    site_id: "",
    site_name: "",
  },

  setSmtpSite: (smtpSite: { site_id: string; site_name: string }) =>
    set({ smtpSite }),

  selectedRowAlertsSMTP: [],
  setSelectedRowAlertsSMTP: (selectedRowAlertsSMTP: any[]) =>
    set({ selectedRowAlertsSMTP }),

  loadingCamerasSMTP: false,
  setLoadingCamerasSMTP: (loadingCamerasSMTP: boolean) =>
    set({ loadingCamerasSMTP }),

  userFiltersSMTP: {
    user: "",
    group_id: null,
    start_date: startDate,
    end_date: endDate,
    start_time: null,
    end_time: null,
    site_ids: [],
    camera_ids: [],
  },
  setUserFiltersSMTP: (userFiltersSMTP: any | ((prev: any) => any)) =>
    set((state) => ({
      userFiltersSMTP:
        typeof userFiltersSMTP === "function"
          ? userFiltersSMTP(state.userFiltersSMTP)
          : userFiltersSMTP,
    })),

  cameraOptionsSMTP: [],
  setCameraOptionsSMTP: (cameraOptionsSMTP: any[]) =>
    set({ cameraOptionsSMTP }),

  timeFiltersDisabledSMTP: false,
  setTimeFiltersDisabledSMTP: (timeFiltersDisabledSMTP: boolean) =>
    set({ timeFiltersDisabledSMTP }),

  pageLoadingSMTP: false,
  setPageLoadingSMTP: (pageLoadingSMTP: boolean) => set({ pageLoadingSMTP }),

  pageSMTP: 0,
  setPageSMTP: (pageSMTP: number) => set({ pageSMTP }),

  videoClipLoadingSMTP: false,
  setVideoClipLoadingSMTP: (videoClipLoadingSMTP: boolean) =>
    set({ videoClipLoadingSMTP }),

  loadingAdditionalDataSMTP: false,
  setLoadingAdditionalDataSMTP: (loadingAdditionalDataSMTP: boolean) =>
    set({ loadingAdditionalDataSMTP }),

  clipsIndexOrderSMTP: {},
  setClipsIndexOrderSMTP: (clipsIndexOrderSMTP: any) =>
    set({ clipsIndexOrderSMTP }),

  allAlertsDialogOpen: false,
  setAllAlertsDialogOpen: (allAlertsDialogOpen: boolean) =>
    set({ allAlertsDialogOpen }),

  falsePositiveReductionDialogOpen: false,
  setFalsePositiveReductionDialogOpen: (
    falsePositiveReductionDialogOpen: boolean
  ) => set({ falsePositiveReductionDialogOpen }),

  requestCounterSMTP: 0,
  setRequestCounterSMTP: (requestCounterSMTP: number) =>
    set({ requestCounterSMTP }),

  downloadURLSMTP: "",
  setDownloadURLSMTP: (downloadURLSMTP: string) => set({ downloadURLSMTP }),

  fetchingDownloadURLSMTP: false,
  setFetchingDownloadURLSMTP: (fetchingDownloadURLSMTP: boolean) =>
    set({ fetchingDownloadURLSMTP }),

  downloadURLAILink: "",
  setDownloadURLAILink: (downloadURLAILink: string) =>
    set({ downloadURLAILink }),

  fetchingDownloadURLAILink: false,
  setFetchingDownloadURLAILink: (fetchingDownloadURLAILink: boolean) =>
    set({ fetchingDownloadURLAILink }),

  downloadURLSentinel: "",
  setDownloadURLSentinel: (downloadURLSentinel: string) =>
    set({ downloadURLSentinel }),

  fetchingDownloadURLSentinel: false,
  setFetchingDownloadURLSentinel: (fetchingDownloadURLSentinel: boolean) =>
    set({ fetchingDownloadURLSentinel }),

  smtpTableFilter: "all",
  setSmtpTableFilter: (smtpTableFilter: string) => set({ smtpTableFilter }),

  siteTimeZoneSMTP: "",
  setSiteTimeZoneSMTP: (siteTimeZoneSMTP: string) => set({ siteTimeZoneSMTP }),

  getUserGroupsSMTP: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getUserGroupsSMTP(navigate);
    };

    const { setGroupOptionsSMTP, setLoadingGroupsSMTP } = get();

    setLoadingGroupsSMTP(true);

    const allGroups: any[] = [];

    // Recursive function to handle pagination
    const recursiveFunction = (apiURL: string) => {
      const AxiosNextUI = createAPIwithNextValue(apiURL);

      AxiosNextUI.get(``)
        .then((response: AxiosResponse) => {
          // Add results to the accumulated data
          allGroups.push(...response.data.results);

          // Check if there's a next page
          if (response.data.next) {
            // Continue with the next page
            recursiveFunction(response.data.next);
          } else {
            // No more pages, set the final data and stop loading
            setGroupOptionsSMTP(allGroups);
            setLoadingGroupsSMTP(false);
          }
        })
        .catch((reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            setGroupOptionsSMTP([]);
            setLoadingGroupsSMTP(false);
          });
        });
    };

    try {
      const response = await AxiosUI.get(`group/?group_type=smtp`);

      // Add first page results
      allGroups.push(...response.data.results);

      // Check if there are more pages
      if (response.data.next) {
        // Continue with pagination
        recursiveFunction(response.data.next);
      } else {
        // No pagination needed, set data and stop loading
        setGroupOptionsSMTP(allGroups);
        setLoadingGroupsSMTP(false);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setGroupOptionsSMTP([]);
          setLoadingGroupsSMTP(false);
        }
      );
    }
  },

  getAllSitesClipsSMTP: async (
    groupID: number | null,
    navigate: NavigateFunction
  ) => {
    if (!groupID) {
      return;
    }

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getAllSitesClipsSMTP(groupID, navigate);
    };

    const { setSiteOptionsSMTP, setLoadingSitesSMTP } = get();

    setSiteOptionsSMTP([]);
    setLoadingSitesSMTP(true);

    try {
      const response = await AxiosUI.get(
        `customer/names/${groupID}/?group_type=smtp`
      );

      setSiteOptionsSMTP(response.data);
      setLoadingSitesSMTP(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setLoadingSitesSMTP(false);
        }
      );
    }
  },

  getCamerasBySiteSMTP: async (
    siteIds: string[],
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getCamerasBySiteSMTP(siteIds, navigate);
    };

    const { setCameraOptionsSMTP, setLoadingCamerasSMTP } = get();

    setLoadingCamerasSMTP(true);

    let idString = "";

    if (siteIds.length === 1) {
      idString = `${siteIds[0]}`;
    }
    if (siteIds.length > 1) {
      idString = `${siteIds[0]}`;
      siteIds.forEach((id: string, index: number) => {
        if (index > 0) {
          idString += `,${id}`;
        }
      });
    }

    let allSiteCameras: any;

    const getSiteCameras = (pageNumber: number) => {
      AxiosUI.get(`camera/site/?customers=${idString}&page=${pageNumber}`).then(
        (response: AxiosResponse) => {
          if (response.data.count > 0) {
            if (pageNumber === 1) {
              allSiteCameras = response.data.results;
            }
            if (pageNumber > 1) {
              allSiteCameras.push(...response.data.results);
            }
            if (response.data.next) {
              getSiteCameras(pageNumber + 1);
            } else {
              setCameraOptionsSMTP(allSiteCameras);
              setLoadingCamerasSMTP(false);
            }
          } else {
            setCameraOptionsSMTP([]);
            setLoadingCamerasSMTP(false);
          }
        },
        (reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            setLoadingCamerasSMTP(false);
          });
        }
      );
    };

    siteIds.length === 0 ? setCameraOptionsSMTP([]) : getSiteCameras(1);
  },

  getSMTPData: async (navigate: NavigateFunction) => {
    const {
      userFiltersSMTP,
      setSmtpData,
      getSMTPData,
      setLoadingAdditionalDataSMTP,
      setSelectedRowSMTP,
      getVideoURLSMTP,
      setNoDataToDisplaySMTP,
      setFetchingFilteredDataSMTP,
      requestCounterSMTP,
      setRequestCounterSMTP,
      setSmtpSite,
    } = get();

    const currentRequestId = requestCounterSMTP + 1;
    setRequestCounterSMTP(currentRequestId);
    const currentRequestToken = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    activeSMTPRequestToken = currentRequestToken;
    const urlClipId = new URLSearchParams(window.location.search).get(
      "clip_id"
    );
    const hasUrlClipSelection =
      window.location.pathname.includes("clips") && !!urlClipId;

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      getSMTPData(navigate);
    };

    // Helper function to format date for API parameter
    const formatDateForAPI = (date: Date) => {
      if (!date) return null;
      return date.toISOString().split("T")[0]; // Get YYYY-MM-DD format
    };

    // Helper function to format time for API parameter
    const formatTimeForAPI = (time: Date | null) => {
      if (!time) return null;
      return time.toTimeString().split(" ")[0].substring(0, 5); // Get HH:MM format
    };

    let searchString = "";

    // if (userFiltersSMTP.user) {
    //   searchString += `&user=${userFiltersSMTP.user}`;
    // }
    if (userFiltersSMTP.site_ids[0] && !userFiltersSMTP.camera_ids[0]) {
      searchString += `customer_id=${userFiltersSMTP.site_ids[0]}`;
    } else if (userFiltersSMTP.camera_ids[0]) {
      searchString += `camera_id=${userFiltersSMTP.camera_ids[0]}`;
    }

    const allData: any[] = [];

    setLoadingAdditionalDataSMTP(true);

    // Add date and time parameters
    const startDate = formatDateForAPI(userFiltersSMTP.start_date);
    const endDate = formatDateForAPI(userFiltersSMTP.end_date);
    const startTime = formatTimeForAPI(userFiltersSMTP.start_time);
    const endTime = formatTimeForAPI(userFiltersSMTP.end_time);

    if (startDate) {
      searchString += searchString
        ? `&start_date=${startDate}`
        : `start_date=${startDate}`;
    }
    if (endDate) {
      searchString += searchString
        ? `&end_date=${endDate}`
        : `end_date=${endDate}`;
    }
    if (startTime) {
      searchString += searchString
        ? `&start_time=${startTime}`
        : `start_time=${startTime}`;
    }
    if (endTime) {
      searchString += searchString
        ? `&end_time=${endTime}`
        : `end_time=${endTime}`;
    }

    // Add direction parameter
    searchString += searchString ? `&direction=next` : `direction=next`;

    const recursiveFunction = (apiURL: string) => {
      const AxiosNextUI = createAPIwithNextValue(apiURL);

      AxiosNextUI.get(``).then((response: AxiosResponse) => {
        if (activeSMTPRequestToken !== currentRequestToken) {
          return;
        }
        // Check if this is still the most recent request
        const { requestCounterSMTP } = get();
        if (requestCounterSMTP !== currentRequestId) {
          // This is an outdated request, ignore the response
          return;
        }

        // Add selected property to each item as we receive it (only first item of first batch is selected)
        const resultsWithSelection = response.data.results.map((item: any) => ({
          ...item,
          selected: false, // Additional batches are not selected
        }));
        allData.push(...resultsWithSelection);

        // Check if we should continue pagination
        // Stop pagination if:
        // 1. No next URL
        // 2. Not a full page (less than 100 results)
        // 3. Not on clips page
        // 4. No results in this batch
        const shouldContinuePagination =
          response.data.next &&
          response.data.count === 100 &&
          window.location.href.includes("clips") &&
          response.data.results.length > 0;

        if (shouldContinuePagination) {
          // Preserve current selection when adding new data
          const currentSmtpData = get().smtpData;
          const currentlySelectedClipId = currentSmtpData.find(
            (item: any) => item.selected
          )?.clip_id;

          // Update allData to preserve the current selection
          const updatedAllData = allData.map((item: any) => ({
            ...item,
            selected: item.clip_id === currentlySelectedClipId,
          }));

          setSmtpData(updatedAllData);
          recursiveFunction(response.data.next);
          setNoDataToDisplaySMTP(false);
          setFetchingFilteredDataSMTP(false);
        } else {
          const currentSmtpData = get().smtpData;
          const currentlySelectedClipId = currentSmtpData.find(
            (item: any) => item.selected
          )?.clip_id;
          const updatedAllData = allData.map((item: any) => ({
            ...item,
            selected: item.clip_id === currentlySelectedClipId,
          }));
          setSmtpData(updatedAllData);
          setLoadingAdditionalDataSMTP(false);
          setNoDataToDisplaySMTP(false);
          setFetchingFilteredDataSMTP(false);
        }
      });
    };

    try {
      const response = await AxiosUI.get(`smtp/?${searchString}`);

      if (activeSMTPRequestToken !== currentRequestToken) {
        return;
      }
      // Check if this is still the most recent request
      const { requestCounterSMTP: currentCounter } = get();
      if (currentCounter !== currentRequestId) {
        // This is an outdated request, ignore the response
        return;
      }

      // Add selected property to each item as we receive it
      const resultsWithSelection = response.data.results.map(
        (item: any, index: number) => ({
          ...item,
          selected: allData.length === 0 && index === 0, // First item of first batch is selected
        })
      );
      allData.push(...resultsWithSelection);

      if (response.data.customer) {
        setSmtpSite({
          site_id: response.data.customer.id,
          site_name: response.data.customer.name,
        });
      }

      if (response.data.count === 0 || allData.length === 0) {
        setNoDataToDisplaySMTP(true);
        setLoadingAdditionalDataSMTP(false);
        setFetchingFilteredDataSMTP(false);
      } else if (response.data.next && response.data.count === 100) {
        if (response.data.results.length > 0) {
          const currentSelectedClipId = get().smtpData.find(
            (item: any) => item.selected
          )?.clip_id;
          const selectedClipId = currentSelectedClipId
            ? currentSelectedClipId
            : hasUrlClipSelection
            ? null
            : allData[0]?.clip_id;
          const updatedAllData = allData.map((item: any) => ({
            ...item,
            selected: selectedClipId ? item.clip_id === selectedClipId : false,
          }));
          setSmtpData(updatedAllData);
          if (!hasUrlClipSelection && selectedClipId && allData.length > 0) {
            const selectedRow = allData.find(
              (item: any) => item.clip_id === selectedClipId
            );
            if (selectedRow) {
              setSelectedRowSMTP(selectedRow);
              getVideoURLSMTP(selectedRow.clip_id, navigate);
            }
          }
          recursiveFunction(response.data.next);
        } else {
          // No results, stop pagination
          setNoDataToDisplaySMTP(false);
          const currentSelectedClipId = get().smtpData.find(
            (item: any) => item.selected
          )?.clip_id;
          const selectedClipId = currentSelectedClipId
            ? currentSelectedClipId
            : hasUrlClipSelection
            ? null
            : allData[0]?.clip_id;
          const updatedAllData = allData.map((item: any) => ({
            ...item,
            selected: selectedClipId ? item.clip_id === selectedClipId : false,
          }));
          setSmtpData(updatedAllData);
          if (!hasUrlClipSelection && selectedClipId && allData.length > 0) {
            const selectedRow = allData.find(
              (item: any) => item.clip_id === selectedClipId
            );
            if (selectedRow) {
              setSelectedRowSMTP(selectedRow);
              getVideoURLSMTP(selectedRow.clip_id, navigate);
            }
          }
          setLoadingAdditionalDataSMTP(false);
          setFetchingFilteredDataSMTP(false);
        }
      } else {
        setNoDataToDisplaySMTP(false);
        const currentSelectedClipId = get().smtpData.find(
          (item: any) => item.selected
        )?.clip_id;
        const selectedClipId = currentSelectedClipId
          ? currentSelectedClipId
          : hasUrlClipSelection
          ? null
          : allData[0]?.clip_id;
        const updatedAllData = allData.map((item: any) => ({
          ...item,
          selected: selectedClipId ? item.clip_id === selectedClipId : false,
        }));
        setSmtpData(updatedAllData);
        if (!hasUrlClipSelection && selectedClipId && allData.length > 0) {
          const selectedRow = allData.find(
            (item: any) => item.clip_id === selectedClipId
          );
          if (selectedRow) {
            setSelectedRowSMTP(selectedRow);
            getVideoURLSMTP(selectedRow.clip_id, navigate);
          }
        }
        setLoadingAdditionalDataSMTP(false);
        setFetchingFilteredDataSMTP(false);
      }
    } catch (reason) {
      if (activeSMTPRequestToken !== currentRequestToken) {
        return;
      }
      // Check if this is still the most recent request before handling error
      const { requestCounterSMTP: currentCounter } = get();
      if (currentCounter !== currentRequestId) {
        // This is an outdated request, ignore the error
        return;
      }

      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSmtpData(allData);
          setFetchingFilteredDataSMTP(false);
        }
      );
    }
  },

  getVideoURLSMTP: async (clip_id: string, navigate: NavigateFunction) => {
    const {
      getVideoURLSMTP,
      setVideoClipLoadingSMTP,
      setSelectedRowAlertsSMTP,
      setSelectedClipSMTPMedia,
    } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getVideoURLSMTP(clip_id, navigate);
    };
    setVideoClipLoadingSMTP(true);
    try {
      const response = await AxiosUI.get(`smtp/clip/${clip_id}`);
      if (response.data.clip.alerts) {
        if (response.data.clip.alerts.length > 0) {
          setSelectedRowAlertsSMTP(response.data.clip.alerts);
        } else {
          setSelectedRowAlertsSMTP([]);
        }
      } else {
        setSelectedRowAlertsSMTP([]);
      }
      if (response.data.clip.filenames) {
        setSelectedClipSMTPMedia(response.data.clip.filenames);
      }
      setVideoClipLoadingSMTP(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setVideoClipLoadingSMTP(false);
        }
      );
    }
  },

  getDownloadURLSMTP: async (navigate: NavigateFunction) => {
    const {
      setDownloadURLSMTP,
      getDownloadURLSMTP,
      setFetchingDownloadURLSMTP,
    } = get();

    setDownloadURLSMTP("");

    setFetchingDownloadURLSMTP(true);

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      getDownloadURLSMTP(navigate);
    };

    const searchParamsString = generateDownloadString();

    try {
      const response = await AxiosUI.get(`smtp/download/${searchParamsString}`);
      setDownloadURLSMTP(response.data.url);
      setFetchingDownloadURLSMTP(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setDownloadURLSMTP("");
          setFetchingDownloadURLSMTP(false);
        }
      );
    }
  },

  getDownloadURLAILink: async (
    navigate: NavigateFunction,
    userFilters: any,
    forceRefresh = false,
    groupID: number | null = null
  ) => {
    const {
      setDownloadURLAILink,
      getDownloadURLAILink,
      setFetchingDownloadURLAILink,
    } = get();

    setDownloadURLAILink("");

    setFetchingDownloadURLAILink(true);

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      getDownloadURLAILink(navigate, userFilters, forceRefresh, groupID);
    };

    let searchParamsString = generateDownloadStringAILink(userFilters, groupID);
    if (forceRefresh) {
      // skip the backend's cached report, eg. when today's report should
      // include clips received since the cache was generated
      searchParamsString += `&force_refresh=true`;
    }

    // Every filter change prefetches a new report, and a filtered report is
    // generated rather than served from cache, so requests overlap. Only the
    // newest request may set or clear the link: an older one finishing (or
    // failing) late must not blank the report for the filters now shown.
    const requestId = ++reportRequestIds.AILink;
    const isLatest = () => requestId === reportRequestIds.AILink;

    try {
      const response = await AxiosUI.get(`clip/report/${searchParamsString}`);
      if (!isLatest()) return;
      setDownloadURLAILink(response.data.url);
      setFetchingDownloadURLAILink(false);
    } catch (reason) {
      if (!isLatest()) return;
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setDownloadURLAILink("");
          setFetchingDownloadURLAILink(false);
        }
      );
    }
  },

  getDownloadURLSentinel: async (
    navigate: NavigateFunction,
    userFiltersSentinel: any,
    forceRefresh = false
  ) => {
    const {
      setDownloadURLSentinel,
      getDownloadURLSentinel,
      setFetchingDownloadURLSentinel,
    } = get();

    setDownloadURLSentinel("");

    setFetchingDownloadURLSentinel(true);

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      getDownloadURLSentinel(navigate, userFiltersSentinel, forceRefresh);
    };

    let searchParamsString =
      generateDownloadStringSentinel(userFiltersSentinel);
    if (forceRefresh) {
      // skip the backend's cached report, eg. when today's report should
      // include clips received since the cache was generated
      searchParamsString += `&force_refresh=true`;
    }

    // Every filter change prefetches a new report, and a filtered report is
    // generated rather than served from cache, so requests overlap. Only the
    // newest request may set or clear the link: an older one finishing (or
    // failing) late must not blank the report for the filters now shown.
    const requestId = ++reportRequestIds.Sentinel;
    const isLatest = () => requestId === reportRequestIds.Sentinel;

    try {
      const response = await AxiosUI.get(`clip/report/${searchParamsString}`);
      if (!isLatest()) return;
      setDownloadURLSentinel(response.data.url);
      setFetchingDownloadURLSentinel(false);
    } catch (reason) {
      if (!isLatest()) return;
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setDownloadURLSentinel("");
          setFetchingDownloadURLSentinel(false);
        }
      );
    }
  },

  showFrontelTab: false,
  setShowFrontelTab: (showFrontelTab: boolean) => set({ showFrontelTab }),

  frontelData: [],
  setFrontelData: (frontelData: any[]) => set({ frontelData }),

  precisionRecallDataFrontel: [],
  setPrecisionRecallDataFrontel: (precisionRecallDataFrontel: any) =>
    set({ precisionRecallDataFrontel }),

  selectedRowFrontel: {},
  setSelectedRowFrontel: (selectedRowFrontel: any) =>
    set({ selectedRowFrontel }),

  selectedClipFrontelMedia: [],
  setSelectedClipFrontelMedia: (selectedClipFrontelMedia: any[]) =>
    set({ selectedClipFrontelMedia }),

  noDataToDisplayFrontel: true,
  setNoDataToDisplayFrontel: (noDataToDisplayFrontel: boolean) =>
    set({ noDataToDisplayFrontel }),

  showMoreDialogOpenFrontel: false,
  setShowMoreDialogOpenFrontel: (showMoreDialogOpenFrontel: boolean) =>
    set({ showMoreDialogOpenFrontel }),

  verifiedClickedFrontel: false,
  setVerifiedClickedFrontel: (verifiedClickedFrontel: boolean) =>
    set({ verifiedClickedFrontel }),

  unverifiedClickedFrontel: false,
  setUnverifiedClickedFrontel: (unverifiedClickedFrontel: boolean) =>
    set({ unverifiedClickedFrontel }),

  filtersAppliedFrontel: false,
  setFiltersAppliedFrontel: (filtersAppliedFrontel: boolean) =>
    set({ filtersAppliedFrontel }),

  fetchingFilteredDataFrontel: false,
  setFetchingFilteredDataFrontel: (fetchingFilteredDataFrontel: boolean) =>
    set({ fetchingFilteredDataFrontel }),

  userDropDownValueFrontel: null,
  setUserDropDownValueFrontel: (userDropDownValueFrontel: string | null) =>
    set({ userDropDownValueFrontel }),

  cameraDropDownValueFrontel: null,
  setCameraDropDownValueFrontel: (cameraDropDownValueFrontel: string | null) =>
    set({ cameraDropDownValueFrontel }),

  groupIDFrontel: null,
  setGroupIDFrontel: (groupIDFrontel: number | null) => set({ groupIDFrontel }),

  frontelUsername: "",
  setFrontelUsername: (frontelUsername: string) => set({ frontelUsername }),

  groupOptionsFrontel: localStorage.parentGroups
    ? JSON.parse(localStorage.parentGroups)
    : [],
  setGroupOptionsFrontel: (groupOptionsFrontel: any[]) =>
    set({ groupOptionsFrontel }),

  loadingGroupsFrontel: false,
  setLoadingGroupsFrontel: (loadingGroupsFrontel: boolean) =>
    set({ loadingGroupsFrontel }),

  siteDropDownValueFrontel: null,
  setSiteDropDownValueFrontel: (siteDropDownValueFrontel: string | null) =>
    set({ siteDropDownValueFrontel }),

  siteOptionsFrontel: [],
  setSiteOptionsFrontel: (siteOptionsFrontel: any[]) =>
    set({ siteOptionsFrontel }),

  loadingSitesFrontel: false,
  setLoadingSitesFrontel: (loadingSitesFrontel: boolean) =>
    set({ loadingSitesFrontel }),

  frontelSite: {
    site_id: "",
    site_name: "",
  },
  setFrontelSite: (frontelSite: { site_id: string; site_name: string }) =>
    set({ frontelSite }),

  selectedRowAlertsFrontel: [],
  setSelectedRowAlertsFrontel: (selectedRowAlertsFrontel: any[]) =>
    set({ selectedRowAlertsFrontel }),

  loadingCamerasFrontel: false,
  setLoadingCamerasFrontel: (loadingCamerasFrontel: boolean) =>
    set({ loadingCamerasFrontel }),

  userFiltersFrontel: {
    user: "",
    start_date: startDate,
    end_date: endDate,
    start_time: null,
    end_time: null,
    site_ids: [],
    camera_ids: [],
  },
  setUserFiltersFrontel: (userFiltersFrontel: any | ((prev: any) => any)) =>
    set((state) => ({
      userFiltersFrontel:
        typeof userFiltersFrontel === "function"
          ? userFiltersFrontel(state.userFiltersFrontel)
          : userFiltersFrontel,
    })),

  cameraOptionsFrontel: [],
  setCameraOptionsFrontel: (cameraOptionsFrontel: any[]) =>
    set({ cameraOptionsFrontel }),

  timeFiltersDisabledFrontel: false,
  setTimeFiltersDisabledFrontel: (timeFiltersDisabledFrontel: boolean) =>
    set({ timeFiltersDisabledFrontel }),

  pageLoadingFrontel: false,
  setPageLoadingFrontel: (pageLoadingFrontel: boolean) =>
    set({ pageLoadingFrontel }),

  pageFrontel: 0,
  setPageFrontel: (pageFrontel: number) => set({ pageFrontel }),

  videoClipLoadingFrontel: false,
  setVideoClipLoadingFrontel: (videoClipLoadingFrontel: boolean) =>
    set({ videoClipLoadingFrontel }),

  videoURLFrontel: "",
  setVideoURLFrontel: (videoURLFrontel: string) => set({ videoURLFrontel }),

  loadingAdditionalDataFrontel: false,
  setLoadingAdditionalDataFrontel: (loadingAdditionalDataFrontel: boolean) =>
    set({ loadingAdditionalDataFrontel }),

  clipsIndexOrderFrontel: {},
  setClipsIndexOrderFrontel: (clipsIndexOrderFrontel: any) =>
    set({ clipsIndexOrderFrontel }),

  requestCounterFrontel: 0,
  setRequestCounterFrontel: (requestCounterFrontel: number) =>
    set({ requestCounterFrontel }),

  getUserGroupsFrontel: async (navigate: NavigateFunction) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getUserGroupsFrontel(navigate);
    };

    const {
      setGroupOptionsFrontel,
      setLoadingGroupsFrontel,
      setShowFrontelTab,
    } = get();

    setLoadingGroupsFrontel(true);

    try {
      const response = await AxiosUI.get(`immix_credential/?user_type=frontel`);
      if (response.data.length > 0) {
        setShowFrontelTab(true);
      }
      setGroupOptionsFrontel(response.data);
      setLoadingGroupsFrontel(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setGroupOptionsFrontel([]);
          setLoadingGroupsFrontel(false);
        }
      );
    }
  },

  getAllSitesFrontel: async (
    groupID: number | null,
    navigate: NavigateFunction
  ) => {
    if (!groupID) {
      return;
    }

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getAllSitesFrontel(groupID, navigate);
    };

    const { setSiteOptionsFrontel, setLoadingSitesFrontel } = get();

    setSiteOptionsFrontel([]);
    setLoadingSitesFrontel(true);

    const allSites: any[] = [];

    // Recursive function to handle pagination
    const recursiveFunction = (apiURL: string) => {
      const AxiosNextUI = createAPIwithNextValue(apiURL);

      AxiosNextUI.get(``)
        .then((response: AxiosResponse) => {
          // Add results to the accumulated data
          allSites.push(...response.data.results);

          // Check if there's a next page
          if (response.data.next) {
            // Continue with the next page
            recursiveFunction(response.data.next);
          } else {
            // No more pages, set the final data and stop loading
            setSiteOptionsFrontel(allSites);
            setLoadingSitesFrontel(false);
          }
        })
        .catch((reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            setSiteOptionsFrontel([]);
            setLoadingSitesFrontel(false);
          });
        });
    };

    try {
      const response = await AxiosUI.get(`customer/names/${groupID}/`);

      // Check if response has results array (paginated) or is the data directly
      if (response.data.results) {
        // Add first page results
        allSites.push(...response.data.results);

        // Check if there are more pages
        if (response.data.next) {
          // Continue with pagination
          recursiveFunction(response.data.next);
        } else {
          // No pagination needed, set data and stop loading
          setSiteOptionsFrontel(allSites);
          setLoadingSitesFrontel(false);
        }
      } else {
        // Response is not paginated, use directly
        setSiteOptionsFrontel(response.data);
        setLoadingSitesFrontel(false);
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteOptionsFrontel([]);
          setLoadingSitesFrontel(false);
        }
      );
    }
  },

  getCamerasBySiteFrontel: async (
    siteIds: string[],
    navigate: NavigateFunction
  ) => {
    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getCamerasBySiteFrontel(siteIds, navigate);
    };

    const { setCameraOptionsFrontel, setLoadingCamerasFrontel } = get();

    setLoadingCamerasFrontel(true);

    let idString = "";

    if (siteIds.length === 1) {
      idString = `${siteIds[0]}`;
    }
    if (siteIds.length > 1) {
      idString = `${siteIds[0]}`;
      siteIds.forEach((id: string, index: number) => {
        if (index > 0) {
          idString += `,${id}`;
        }
      });
    }

    let allSiteCameras: any;

    const getSiteCamerasFrontel = (pageNumber: number) => {
      AxiosUI.get(`camera/site/?customers=${idString}&page=${pageNumber}`).then(
        (response: AxiosResponse) => {
          if (response.data.count > 0) {
            if (pageNumber === 1) {
              allSiteCameras = response.data.results;
            }
            if (pageNumber > 1) {
              allSiteCameras.push(...response.data.results);
            }
            if (response.data.next) {
              getSiteCamerasFrontel(pageNumber + 1);
            } else {
              setCameraOptionsFrontel(allSiteCameras);
              setLoadingCamerasFrontel(false);
            }
          } else {
            setCameraOptionsFrontel([]);
            setLoadingCamerasFrontel(false);
          }
        },
        (reason: AxiosError) => {
          universalAPIErrorHandler(reason, navigate, retryFunction, () => {
            setLoadingCamerasFrontel(false);
          });
        }
      );
    };

    siteIds.length === 0
      ? setCameraOptionsFrontel([])
      : getSiteCamerasFrontel(1);
  },

  getFrontelData: async (navigate: NavigateFunction) => {
    const {
      userFiltersFrontel,
      setFrontelData,
      getFrontelData,
      setLoadingAdditionalDataFrontel,
      setSelectedRowFrontel,
      getClipInfoFrontel,
      getVideoURLFrontel,
      setNoDataToDisplayFrontel,
      setFetchingFilteredDataFrontel,
      requestCounterFrontel,
      setRequestCounterFrontel,
      setFrontelSite,
      frontelUsername,
      setPrecisionRecallDataFrontel,
    } = get();

    // Increment the request counter for this call
    const currentRequestId = requestCounterFrontel + 1;
    setRequestCounterFrontel(currentRequestId);

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      getFrontelData(navigate);
    };

    // Helper function to format date for API parameter
    const formatDateForAPI = (date: Date) => {
      if (!date) return null;
      return date.toISOString().split("T")[0]; // Get YYYY-MM-DD format
    };

    // Helper function to format time for API parameter
    const formatTimeForAPI = (time: Date | null) => {
      if (!time) return null;
      return time.toTimeString().split(" ")[0].substring(0, 5); // Get HH:MM format
    };

    let searchString = "";

    if (userFiltersFrontel.site_ids[0] && !userFiltersFrontel.camera_ids[0]) {
      searchString += `customer_id=${userFiltersFrontel.site_ids[0]}`;
    } else if (userFiltersFrontel.camera_ids[0]) {
      searchString += `camera_id=${userFiltersFrontel.camera_ids[0]}`;
    }

    const allData: any[] = [];

    setLoadingAdditionalDataFrontel(true);

    // Add date and time parameters
    const startDate = formatDateForAPI(userFiltersFrontel.start_date);
    const endDate = formatDateForAPI(userFiltersFrontel.end_date);
    const startTime = formatTimeForAPI(userFiltersFrontel.start_time);
    const endTime = formatTimeForAPI(userFiltersFrontel.end_time);

    if (startDate) {
      searchString += searchString
        ? `&start_date=${startDate}`
        : `start_date=${startDate}`;
    }
    if (endDate) {
      searchString += searchString
        ? `&end_date=${endDate}`
        : `end_date=${endDate}`;
    }
    if (startTime) {
      searchString += searchString
        ? `&start_time=${startTime}`
        : `start_time=${startTime}`;
    }
    if (endTime) {
      searchString += searchString
        ? `&end_time=${endTime}`
        : `end_time=${endTime}`;
    }

    // The clip list endpoint does not return a DRF-style `next` URL — it
    // returns cursor keys (`last_timestamp` / `last_file_id`) and `count` is
    // the number of results in THIS page (page_size defaults to 100). A full
    // page (count === 100) with cursor keys means there may be more data.
    const recursiveFunction = (lastTimestamp: string, lastFileId: string) => {
      AxiosUI.get(
        `clip/?username=${frontelUsername}&${searchString}` +
          `&last_timestamp=${encodeURIComponent(lastTimestamp)}` +
          `&last_file_id=${encodeURIComponent(lastFileId)}`
      ).then(
        (response: AxiosResponse) => {
          // Check if this is still the most recent request
          const { requestCounterFrontel } = get();
          if (requestCounterFrontel !== currentRequestId) {
            // This is an outdated request, ignore the response
            return;
          }

          // Add selected property to each item as we receive it (only first item of first batch is selected)
          const resultsWithSelection = response.data.results.map(
            (item: any) => ({
              ...item,
              selected: false, // Additional batches are not selected
            })
          );
          allData.push(...resultsWithSelection);

          // Fold this page's per-day summary into the accumulated totals
          if (response.data.summary && response.data.summary.length > 0) {
            setPrecisionRecallDataFrontel(
              mergeFrontelPrecisionRecallSummaries(
                get().precisionRecallDataFrontel,
                response.data.summary
              )
            );
          }

          // Preserve current selection when adding new data
          const currentFrontelData = get().frontelData;
          const currentlySelectedClipId = currentFrontelData.find(
            (item: any) => item.selected
          )?.file_id;

          // Update allData to preserve the current selection
          const updatedAllData = allData.map((item: any) => ({
            ...item,
            selected: item.file_id === currentlySelectedClipId,
          }));

          // Check if we should continue pagination
          const shouldContinuePagination =
            response.data.count === 100 &&
            response.data.last_timestamp &&
            response.data.last_file_id &&
            window.location.href.includes("clips") &&
            response.data.results.length > 0;

          if (shouldContinuePagination) {
            setFrontelData(updatedAllData);
            recursiveFunction(
              response.data.last_timestamp,
              response.data.last_file_id
            );
            setNoDataToDisplayFrontel(false);
            setFetchingFilteredDataFrontel(false);
          } else {
            setFrontelData(updatedAllData);
            setLoadingAdditionalDataFrontel(false);
            setNoDataToDisplayFrontel(false);
            setFetchingFilteredDataFrontel(false);
          }
        },
        () => {
          // A failed additional page should not strand the loading spinner —
          // keep whatever data was already accumulated.
          const { requestCounterFrontel } = get();
          if (requestCounterFrontel !== currentRequestId) {
            return;
          }
          setLoadingAdditionalDataFrontel(false);
          setFetchingFilteredDataFrontel(false);
        }
      );
    };

    try {
      const response = await AxiosUI.get(
        `clip/?username=${frontelUsername}&${searchString}`
      );

      // Check if this is still the most recent request
      const { requestCounterFrontel: currentCounter } = get();
      if (currentCounter !== currentRequestId) {
        // This is an outdated request, ignore the response
        return;
      }

      // Add selected property to each item as we receive it
      const resultsWithSelection = response.data.results.map(
        (item: any, index: number) => ({
          ...item,
          selected: allData.length === 0 && index === 0, // First item of first batch is selected
        })
      );
      allData.push(...resultsWithSelection);

      if (response.data.customer) {
        setFrontelSite({
          site_id: response.data.customer.id,
          site_name: response.data.customer.name,
        });
      }

      // Set precision and recall data from response summary (raw array for
      // daily breakdown). Always reset here so a new request never merges
      // additional pages into a previous request's totals.
      setPrecisionRecallDataFrontel(response.data.summary || []);

      if (response.data.count === 0 || allData.length === 0) {
        setNoDataToDisplayFrontel(true);
        setLoadingAdditionalDataFrontel(false);
        setFetchingFilteredDataFrontel(false);
      } else if (
        response.data.count === 100 &&
        response.data.last_timestamp &&
        response.data.last_file_id
      ) {
        // Full page — continue fetching with the cursor keys the endpoint
        // returns (it has no DRF-style `next` URL).
        setNoDataToDisplayFrontel(false);
        setFrontelData(allData);
        if (allData.length > 0) {
          setSelectedRowFrontel(allData[0]);
          getClipInfoFrontel(allData[0].file_id, navigate);
          getVideoURLFrontel(allData[0].file_id, navigate);
        }
        recursiveFunction(
          response.data.last_timestamp,
          response.data.last_file_id
        );
      } else {
        setNoDataToDisplayFrontel(false);
        setFrontelData(allData);
        if (allData.length > 0) {
          setSelectedRowFrontel(allData[0]);
          getClipInfoFrontel(allData[0].file_id, navigate);
          getVideoURLFrontel(allData[0].file_id, navigate);
        }
        setLoadingAdditionalDataFrontel(false);
        setFetchingFilteredDataFrontel(false);
      }
    } catch (reason) {
      // Check if this is still the most recent request before handling error
      const { requestCounterFrontel: currentCounter } = get();
      if (currentCounter !== currentRequestId) {
        // This is an outdated request, ignore the error
        return;
      }

      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setFrontelData(allData);
          setFetchingFilteredDataFrontel(false);
        }
      );
    }
  },

  getClipInfoFrontel: async (file_id: string, navigate: NavigateFunction) => {
    // The clip list endpoint only returns summary fields — detail fields such
    // as internal_id (Internal Log ID), fps, slice and stationary_filter only
    // come back from clip/info/. Fetch them and merge into the selected row,
    // matching how the AILink and Sentinel tabs enrich their selection.
    const { getClipInfoFrontel, frontelUsername } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getClipInfoFrontel(file_id, navigate);
    };
    try {
      const response = await AxiosUI.get(
        `clip/info/?username=${frontelUsername}&file_id=${file_id}`
      );
      if (response.data && response.data.file_id) {
        const { selectedRowFrontel, setSelectedRowFrontel } = get();
        // Only merge if this clip is still the selected one (the user may
        // have clicked another row while the request was in flight)
        if (selectedRowFrontel?.file_id === response.data.file_id) {
          const updatedRow = { ...selectedRowFrontel, ...response.data };
          setSelectedRowFrontel(updatedRow);
          localStorage.setItem("clipsSelectedRow", JSON.stringify(updatedRow));
        }
      }
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          // Keep the list-row data if the detail fetch fails
        }
      );
    }
  },

  getVideoURLFrontel: async (file_id: string, navigate: NavigateFunction) => {
    const {
      getVideoURLFrontel,
      setVideoClipLoadingFrontel,
      setVideoURLFrontel,
      frontelUsername,
    } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getVideoURLFrontel(file_id, navigate);
    };
    setVideoClipLoadingFrontel(true);
    try {
      const response = await AxiosUI.get(
        `clip/url/?username=${frontelUsername}&file_id=${file_id}`
      );
      setVideoURLFrontel(response.data);
      setVideoClipLoadingFrontel(false);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setVideoClipLoadingFrontel(false);
        }
      );
    }
  },

  getSiteTimeZone: async (site_id: string, navigate: NavigateFunction) => {
    const { getSiteTimeZone, setSiteTimeZoneSMTP } = get();
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      getSiteTimeZone(site_id, navigate);
    };
    try {
      const response = await AxiosUI.get(`customer/${site_id}/about`);
      setSiteTimeZoneSMTP(response.data.timezone);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteTimeZoneSMTP("");
        }
      );
    }
  },

  resetState: () =>
    set({
      // SMTP state
      smtpData: [],
      precisionRecallDataSMTP: {},
      selectedRowSMTP: {},
      selectedClipSMTPMedia: [],
      activeClipsTab: "",
      noDataToDisplaySMTP: true,
      filtersAppliedSMTP: false,
      fetchingFilteredDataSMTP: false,
      userDropDownValueSMTP: null,
      cameraDropDownValueSMTP: null,
      groupIDSMTP: null,
      groupOptionsSMTP: [],
      loadingGroupsSMTP: false,
      siteDropDownValueSMTP: null,
      siteOptionsSMTP: [],
      loadingSitesSMTP: false,
      loadingCamerasSMTP: false,
      userFiltersSMTP: {
        user: "",
        group_id: null,
        start_date: startDate,
        end_date: endDate,
        start_time: null,
        end_time: null,
        site_ids: [],
        camera_ids: [],
      },
      cameraOptionsSMTP: [],
      timeFiltersDisabledSMTP: false,
      pageLoadingSMTP: false,
      pageSMTP: 0,
      videoClipLoadingSMTP: false,
      loadingAdditionalDataSMTP: false,
      clipsIndexOrderSMTP: {},
      allAlertsDialogOpen: false,
      falsePositiveReductionDialogOpen: false,
      requestCounterSMTP: 0,
      downloadURLSMTP: "",
      downloadURLAILink: "",
      downloadURLSentinel: "",
      smtpTableFilter: "all",
      siteTimeZoneSMTP: "",
      // Frontel state
      frontelData: [],
      precisionRecallDataFrontel: [],
      selectedRowFrontel: {},
      selectedClipFrontelMedia: [],
      noDataToDisplayFrontel: true,
      showMoreDialogOpenFrontel: false,
      verifiedClickedFrontel: false,
      unverifiedClickedFrontel: false,
      filtersAppliedFrontel: false,
      fetchingFilteredDataFrontel: false,
      userDropDownValueFrontel: null,
      cameraDropDownValueFrontel: null,
      groupIDFrontel: null,
      frontelUsername: "",
      // do not reset groupOptionsFrontel - it is loaded at login and must persist when opening Clips
      loadingGroupsFrontel: false,
      siteDropDownValueFrontel: null,
      siteOptionsFrontel: [],
      loadingSitesFrontel: false,
      loadingCamerasFrontel: false,
      frontelSite: {
        site_id: "",
        site_name: "",
      },
      selectedRowAlertsFrontel: [],
      userFiltersFrontel: {
        user: "",
        start_date: startDate,
        end_date: endDate,
        start_time: null,
        end_time: null,
        site_ids: [],
        camera_ids: [],
      },
      cameraOptionsFrontel: [],
      timeFiltersDisabledFrontel: false,
      pageLoadingFrontel: false,
      pageFrontel: 0,
      videoClipLoadingFrontel: false,
      videoURLFrontel: "",
      loadingAdditionalDataFrontel: false,
      clipsIndexOrderFrontel: {},
      requestCounterFrontel: 0,
    }),
}));
